"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import { PillNav } from "@/components/nav/pill-nav";
import { NodePair } from "@/components/v2/node-pair";
import { RasterField } from "@/components/v2/raster-field";
import { submitBetaRequest } from "@/lib/api/beta";
import landingTextsFallback from "@/config/landing-texts.json";

import "./landing.css";

/* ══════════════════════════════════════════════════════════════
   Alister AI landing — design system v2 ("Dune").

   Layout, tokens and motion come from `design_handoff_alister_landing/`
   (landing-static.html + tokens/*). Copy still comes from the admin-editable
   landing texts API, so every string below is a `t("…")` lookup with the
   JSON fallback — do not inline copy here.
══════════════════════════════════════════════════════════════ */

/* Marquee ticker — proof signals, not vanity metrics. */
const TICKER =
  "Search developers inside Claude · MCP connector · 100k+ Czech developers · Enriched beyond the CV · Real code, not keywords · Ecosystem reports coming ·";

/**
 * Render text with simple markup, used for admin-editable copy.
 *   *italic*    → <em>          (red in headlines via CSS)
 *   **bold**    → <strong>
 *   __blue__    → <span al-hl>  (blue accent)
 *   newlines    → <br>
 * Order in the regex matters: ** before *, otherwise ** would partial-match.
 */
function renderMarkup(text: string): React.ReactNode[] {
  return text.split("\n").flatMap((line, li, lines) => {
    const parts = line.split(/(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*)/g);
    const nodes = parts.map((part, pi) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return <span key={`${li}-${pi}`} className="al-hl">{part.slice(2, -2)}</span>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={`${li}-${pi}`}>{part.slice(1, -1)}</em>;
      }
      return <span key={`${li}-${pi}`}>{part}</span>;
    });
    if (li < lines.length - 1) nodes.push(<br key={`br-${li}`} />);
    return nodes;
  });
}

/* Collage photography, grayscale and high contrast per the design system.
   Files live in `public/branding/why/`; when one is absent the frame falls
   back to its placeholder rather than showing a broken image. */
function PhotoFill({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img className="al-frame-photo" src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
  );
}

function ArrowIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function HomePage() {
  const router = useRouter();
  const { user, isAdmin, isLoading } = useAuth();

  const [betaName, setBetaName] = useState("");
  const [betaCompany, setBetaCompany] = useState("");
  const [betaEmail, setBetaEmail] = useState("");
  const [betaNeed, setBetaNeed] = useState("");
  const [betaSubmitted, setBetaSubmitted] = useState(false);
  const [betaSending, setBetaSending] = useState(false);
  const [betaError, setBetaError] = useState<string | null>(null);

  const t = (key: string) => landingTextsFallback[key as keyof typeof landingTextsFallback] ?? "";

  const requireAuth = (destination: string) => {
    if (!user && !isLoading) {
      router.push(`/login?next=${encodeURIComponent(destination)}`);
    } else {
      router.push(destination);
    }
  };

  const submitBeta = async () => {
    const email = betaEmail.trim();
    const name = betaName.trim();
    const company = betaCompany.trim();
    if (!email || !name || !company || betaSending) return;
    setBetaSending(true);
    setBetaError(null);
    try {
      await submitBetaRequest({ name, company, email, need: betaNeed.trim() });
      setBetaSubmitted(true);
    } catch (e) {
      setBetaError(e instanceof Error ? e.message : "Failed to send request.");
    } finally {
      setBetaSending(false);
    }
  };

  /* Scroll reveal — sections rise in once, 22–26px with a fade. */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("al-in");
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".al-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const HOW_STEPS = [
    { num: "01", color: "var(--al-blue)", title: t("step1_title"), desc: t("step1_desc") },
    { num: "02", color: "var(--al-amber)", title: t("step2_title"), desc: t("step2_desc") },
    { num: "03", color: "var(--al-red)", title: t("step3_title"), desc: t("step3_desc") },
    { num: "04", color: "var(--al-ink)", title: t("step4_title"), desc: t("step4_desc") },
  ];

  /* The service and what powers it. Card 04 is the only one not shipped yet —
     it carries the "In build" status and links to the reports coming-soon page,
     so the promise on this page has somewhere to land. */
  const APART = [
    { n: "01", color: "var(--al-blue)", status: "Live", title: t("mcp_outcome1_title"), desc: t("mcp_outcome1_desc"), href: null },
    { n: "02", color: "var(--al-amber)", status: "Live", title: t("mcp_outcome2_title"), desc: t("mcp_outcome2_desc"), href: null },
    { n: "03", color: "var(--al-red)", status: "Live", title: t("mcp_outcome3_title"), desc: t("mcp_outcome3_desc"), href: null },
    { n: "04", color: "var(--al-ink)", status: "In build", title: t("mcp_outcome4_title"), desc: t("mcp_outcome4_desc"), href: "/reports" },
  ];

  /* The collage staggers card tops (0 / 18 / 52 / 78px) on purpose.
     Photo frames are placeholders — the handoff shipped no photography. */
  const VALUES = [
    { n: "01", color: "var(--al-blue)", offset: 52, frame: 250, title: t("value1_title"), desc: t("value1_desc"), hint: "code, workshop, hands on keyboard", photo: "/branding/why/01-code.jpg" },
    { n: "02", color: "var(--al-amber)", offset: 0, frame: 280, title: t("value2_title"), desc: t("value2_desc"), hint: "portrait, desk, monitor", photo: "/branding/why/02-order.jpg" },
    { n: "03", color: "var(--al-red)", offset: 78, frame: 230, title: t("value3_title"), desc: t("value3_desc"), hint: "conversation, grid, structure", photo: "/branding/why/03-conversation.jpg" },
    { n: "04", color: "var(--al-ink)", offset: 18, frame: 268, title: t("value4_title"), desc: t("value4_desc"), hint: "Prague, office, ecosystem", photo: "/branding/why/04-city.jpg" },
  ];

  return (
    <div className="al-v2 al-landing">
      <RasterField />
      <PillNav variant="landing" />

      {/* ══ HERO ══ */}
      <section className="al-hero">
        <div
          className="al-orn"
          style={{ right: "-160px", bottom: "-40px", width: "min(50vw,600px)", height: "min(50vw,600px)", animation: "al-drift 18s ease-in-out infinite" }}
        >
          <NodePair fill="#DDD8CE" />
        </div>
        <div
          className="al-orn"
          style={{ left: "-60px", bottom: "9vh", width: "330px", height: "322px", mixBlendMode: "multiply", animation: "al-drift 26s ease-in-out infinite" }}
        >
          <NodePair fill="#056CC1" />
        </div>
        {/* The amber satellite was cropped into the corner because the old
            headline ran the full measure and the circle landed on the words.
            "AI sourcing for / Czech technical hiring." is narrower, so the
            satellite comes back into the composition, clear of the text. */}
        <div
          className="al-orn"
          style={{ right: "7vw", top: "13vh", width: "min(20vw,220px)", height: "min(20vw,220px)", borderRadius: "50%", background: "var(--al-amber)", mixBlendMode: "multiply" }}
        />
        <div
          className="al-orn"
          style={{ left: "15vw", top: "96px", width: "34px", height: "34px", borderRadius: "50%", background: "var(--al-red)" }}
        />

        <div className="al-wrap" style={{ paddingTop: "4vh" }}>
          <span style={{ display: "inline-flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <span className="al-kicker al-kicker-blue">
              <span className="al-kicker-dot" />
              {t("eyebrow")}
            </span>
          </span>

          <h1>{renderMarkup(t("headline"))}</h1>

          <p className="al-lead">{renderMarkup(t("subheadline"))}</p>

          {/* Why the results are different. Secondary to the sourcing promise,
              and on its own panel because the raster eats unbacked body copy. */}
          <p className="al-sub">{renderMarkup(t("hero_support"))}</p>

          <div className="al-hero-actions">
            <button type="button" className="al-btn" onClick={() => requireAuth("/settings")}>
              {t("hero_cta")}
            </button>
            <span className="al-hero-note">{t("hero_note")}</span>
          </div>
        </div>

        <div className="al-wrap al-ticker">
          <div>
            <span>{TICKER}</span>
            <span>{TICKER}</span>
          </div>
        </div>
      </section>

      {/* ══ BETA PROGRAMME ══ */}
      <section id="beta" className="al-reveal" style={{ padding: "100px 28px 90px" }}>
        <div className="al-wrap">
          <div className="al-sechead">
            <span className="k">Beta programme</span>
            <span className="r" />
          </div>
          <div className="al-beta">
            <div>
              <h2>{renderMarkup(t("beta_title"))}</h2>
              <p className="sub">{renderMarkup(t("beta_text"))}</p>
            </div>

            <div className="al-card al-beta-card">
              {!betaSubmitted ? (
                <>
                  <span className="k">{t("beta_form_label")}</span>
                  <div className="al-beta-item">
                    <span className="a">01</span>
                    <span>{t("beta_criteria_1")}</span>
                  </div>
                  <div className="al-beta-item">
                    <span className="a">02</span>
                    <span>{t("beta_criteria_2")}</span>
                  </div>
                  <div className="al-beta-item">
                    <span className="a">03</span>
                    <span>{t("beta_criteria_3")}</span>
                  </div>
                  <div className="al-beta-item">
                    <span className="a">04</span>
                    <span>{t("beta_criteria_4")}</span>
                  </div>

                  <div className="al-beta-form">
                    <div className="al-beta-row">
                      <input
                        type="text"
                        className="al-beta-input"
                        placeholder={t("beta_name_placeholder")}
                        value={betaName}
                        onChange={(e) => setBetaName(e.target.value)}
                        autoComplete="name"
                        required
                      />
                      <input
                        type="text"
                        className="al-beta-input"
                        placeholder={t("beta_company_placeholder")}
                        value={betaCompany}
                        onChange={(e) => setBetaCompany(e.target.value)}
                        autoComplete="organization"
                        required
                      />
                    </div>
                    <input
                      type="email"
                      className="al-beta-input"
                      placeholder={t("beta_email_placeholder")}
                      value={betaEmail}
                      onChange={(e) => setBetaEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                    <textarea
                      className="al-beta-textarea"
                      placeholder={t("beta_need_placeholder")}
                      rows={2}
                      value={betaNeed}
                      onChange={(e) => setBetaNeed(e.target.value)}
                    />
                    <button
                      type="button"
                      className="al-btn al-btn-beta"
                      onClick={submitBeta}
                      disabled={betaSending || !betaName.trim() || !betaCompany.trim() || !betaEmail.trim()}
                    >
                      {betaSending ? "Sending…" : t("beta_btn_label")}
                      {!betaSending && <ArrowIcon />}
                    </button>
                    {betaError && <p className="al-beta-error">{betaError}</p>}
                    <div className="al-beta-note">5-minute setup · no API key</div>
                  </div>
                </>
              ) : (
                <div className="al-beta-thanks">
                  <div className="al-beta-check">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <div className="al-beta-thanks-title">{t("beta_thanks_title")}</div>
                  <div className="al-beta-thanks-sub">{t("beta_thanks_sub")}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHAT SETS ALISTER APART ══ */}
      <section id="apart" className="al-reveal" style={{ padding: "20px 28px 100px" }}>
        <div className="al-wrap">
          <div className="al-sechead">
            <span className="k">{t("mcp_eyebrow")}</span>
            <span className="r" />
          </div>
          <h2>{renderMarkup(t("mcp_title"))}</h2>
          <p className="al-secp">{renderMarkup(t("mcp_subtitle"))}</p>
          <div className="al-grid4">
            {APART.map((c) => {
              const body = (
                <>
                  <span className="al-cardtop">
                    <span className="al-num" style={{ background: c.color }}>{c.n}</span>
                    <span className={`al-status${c.status === "Live" ? " al-status-live" : ""}`}>{c.status}</span>
                  </span>
                  <div className="t">{c.title}</div>
                  <div className="b">{c.desc}</div>
                  {c.href && (
                    <span className="al-cardlink">
                      What's coming
                      <ArrowIcon size={12} />
                    </span>
                  )}
                </>
              );
              return c.href ? (
                <a key={c.n} className="al-card" href={c.href}>{body}</a>
              ) : (
                <div key={c.n} className="al-card">{body}</div>
              );
            })}
          </div>
          <button type="button" className="al-btn al-btn-ghost" style={{ marginTop: "26px" }} onClick={() => requireAuth("/settings")}>
            {t("mcp_cta")}
          </button>
        </div>
      </section>

      {/* ══ LIVE FROM CLAUDE.AI — the one inversion ══ */}
      <section id="chat" className="al-chat">
        <div className="al-chat-head">
          <span className="al-kicker al-kicker-amber">
            <span className="d" />
            Alister · live from Claude.ai
          </span>
        </div>
        <div className="al-thread">
          <div className="al-turn al-t1 you">
            <div className="al-bub you">
              <div className="lbl">You</div>
              <div className="txt">
                <span className="al-typed">Find me a senior Python engineer with FastAPI experience</span>
              </div>
            </div>
          </div>

          <div className="al-turn al-t2">
            <div className="al-bub alister blue">
              <div className="lbl">Alister · search_profiles</div>
              <div className="txt">Here&apos;s the top 10. Leading candidate:</div>
              <div className="al-row">
                <span className="av" style={{ background: "var(--al-red)" }}>JN</span>
                <span>
                  <span className="nm">J. Novák</span>
                  <span className="mt">Senior Backend · 9 yrs · Avast</span>
                </span>
                <span className="tags">
                  <span>Python</span>
                  <span>FastAPI</span>
                  <span>Postgres</span>
                </span>
              </div>
              <div className="follow">Mix of backend and full-stack. Want backend only?</div>
            </div>
          </div>

          <div className="al-turn al-t3 you">
            <div className="al-bub you">
              <div className="lbl">You</div>
              <div className="txt">
                Backend only, 5–10 yrs, skip anyone still at Avast
                <span className="al-caret" />
              </div>
            </div>
          </div>

          <div className="al-turn al-t4">
            <div className="al-bub alister red">
              <div className="lbl">Alister · search_profiles</div>
              <div className="txt">Refined top 10, M. Procházka leads now:</div>
              <div className="al-row">
                <span className="av" style={{ background: "var(--al-blue)" }}>MP</span>
                <span>
                  <span className="nm">M. Procházka</span>
                  <span className="mt">Senior Backend · 7 yrs · Productboard</span>
                </span>
                <span className="tags">
                  <span>Python</span>
                  <span>FastAPI</span>
                  <span>Async</span>
                </span>
              </div>
              <div className="follow">Want me to draft a first message to her?</div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SEARCH FALLBACK BAND — admin-only; everyone else is MCP-focused ══ */}
      {isAdmin && (
        <section className="al-reveal" style={{ padding: "70px 28px 0" }}>
          <div className="al-wrap">
            <button type="button" className="al-card al-fallback" onClick={() => requireAuth("/search")}>
              <span className="al-fallback-copy">
                <span className="e">{t("search_fb_eyebrow")}</span>
                <span className="t">{t("search_fb_title")}</span>
                <span className="s">{renderMarkup(t("search_fb_sub"))}</span>
              </span>
              <span className="al-kicker al-kicker-outline">
                {t("search_fb_cta")}
                <ArrowIcon size={12} />
              </span>
            </button>
          </div>
        </section>
      )}

      {/* ══ HOW IT WORKS ══ */}
      <section id="how" className="al-reveal" style={{ padding: "100px 28px" }}>
        <div className="al-wrap">
          <div className="al-sechead">
            <span className="k">{t("feature_eyebrow")}</span>
            <span className="r" />
          </div>
          <h2>{renderMarkup(t("feature_title"))}</h2>
          <p className="al-secp">{renderMarkup(t("feature_subtitle"))}</p>
          <div className="al-steps">
            {HOW_STEPS.map((step) => (
              <div key={step.num} className="al-card al-step">
                <div className="al-stepn" style={{ color: step.color }}>{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TALENT MAPPING — the other use of the same data layer ══ */}
      <section id="mapping" className="al-reveal" style={{ padding: "20px 28px 100px" }}>
        <div className="al-wrap">
          <div className="al-sechead">
            <span className="k">{t("mapping_eyebrow")}</span>
            <span className="r" />
          </div>
          <div className="al-map">
            <div className="al-map-copy">
              <h2>{renderMarkup(t("mapping_title"))}</h2>
              <p>{renderMarkup(t("mapping_lead"))}</p>
              <div className="al-map-actions">
                <Link href="/blog" className="al-map-link">
                  {t("mapping_link_reports")} <span>&rarr;</span>
                </Link>
                <Link href="/contact" className="al-map-link">
                  {t("mapping_link_ask")} <span>&rarr;</span>
                </Link>
              </div>
            </div>
            <aside className="al-map-note">
              <span className="k">{t("mapping_note_title")}</span>
              <p>{renderMarkup(t("mapping_note_body"))}</p>
            </aside>
          </div>
        </div>
      </section>

      {/* ══ WHY ALISTER AI ══ */}
      <section id="why" className="al-why al-reveal">
        <div className="al-wrap">
          <div className="al-sechead">
            <span className="k">Why Alister AI</span>
            <span className="r" />
          </div>
          <div className="al-cgrid">
            {VALUES.map((v) => (
              <div key={v.n} className="al-card al-collage" style={{ marginTop: `${v.offset}px` }}>
                <div className="pad">
                  <span className="al-num" style={{ background: v.color }}>{v.n}</span>
                  <div className="ct">{v.title}</div>
                  <div className="cb">{v.desc}</div>
                </div>
                <div className="al-frame" style={{ height: `${v.frame}px`, borderTop: `4px solid ${v.color}` }}>
                  {/* Placeholder underneath; the photo covers it once the file
                      exists, and uncovers it again if the file is missing. */}
                  <span className="phk">{v.n} · Photo</span>
                  <span className="phl">{v.hint}</span>
                  <PhotoFill src={v.photo} alt={v.title} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="al-footer">
        <div className="al-wrap">
          <div className="al-fbar">
            <span className="d" style={{ background: "var(--al-blue)" }} />
            <span className="l" style={{ background: "var(--al-blue)" }} />
            <span className="d" style={{ background: "var(--al-amber)" }} />
            <span className="l" style={{ background: "var(--al-beige-deep)" }} />
            <span className="d" style={{ background: "var(--al-red)" }} />
          </div>

          <div className="al-fcols">
            <div>
              <div className="al-fbrand">
                <Image src="/branding/alister-symbol.png" alt="Alister AI" width={32} height={31} />
                <span>Alister AI</span>
              </div>
              <p className="al-ftag">{t("footer_tagline")}</p>
            </div>

            <div>
              <div className="h" style={{ color: "var(--al-blue)" }}>Product</div>
              <div className="links">
                <a href="/settings">MCP Connection</a>
                {isAdmin && <a href="/search">Search</a>}
                
                <a href="/reports">Ecosystem reports</a>
              </div>
            </div>

            <div>
              <div className="h" style={{ color: "var(--al-amber)" }}>Company</div>
              <div className="links">
                <a href="/about">About</a>
                <a href="/blog">Blog</a>
                <a href="/contact">Contact</a>
              </div>
            </div>

            <div className="al-factions">
              <button type="button" className="al-btn al-btn-ghost" onClick={() => requireAuth("/settings")}>
                MCP setup guide
              </button>
              <a className="al-btn" href="/contact">Contact us</a>
            </div>
          </div>

          <div className="al-fbot">
            <span>© 2026 Alister AI · an internal Miton tool</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
